import {
  SEGMENT_COMBINATORS,
  SEGMENT_COUNT_STATES,
  SEGMENT_DATA_TYPES,
  SEGMENT_EVENT_ASPECTS,
  SEGMENT_FIELD_CATEGORIES,
  SEGMENT_FIELD_KINDS,
  SEGMENT_PREVIEW_FAILURES,
  SEGMENT_PREVIEW_STATES,
  SEGMENT_REFRESH_ERROR_CODES,
  SEGMENT_SOURCES,
  SEGMENT_STATUSES,
  type SegmentCondition,
  type SegmentCount,
  type SegmentCreate,
  type SegmentDetail,
  type SegmentField,
  type SegmentFieldEvent,
  type SegmentGroup,
  type SegmentNode,
  type SegmentPreview,
  type SegmentSummary,
  type SegmentUpdate,
} from './types';

/**
 * The segmentation routes' wire, snake_case, to and from the client's types. Decoding is tolerant: a field the
 * client does not know is ignored, and a value it does not know becomes `'unknown'` or a safe default, never an
 * exception. Encoding sends only what the routes take, so a strict route schema never refuses a stray key.
 */

type Wire = Record<string, unknown>;

const objectOf = (value: unknown): Wire => (typeof value === 'object' && value !== null ? (value as Wire) : {});

const text = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);

// A function declaration, not an arrow: Studio's tests read `.ts` as TSX, where `<T>(…) =>` parses as a tag.
function oneOf<T extends string>(allowed: readonly T[], value: unknown): T | 'unknown' {
  return allowed.includes(value as T) ? (value as T) : 'unknown';
}

function oneOfOr<T extends string>(allowed: readonly T[], value: unknown, fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

/** A blank string says nothing, so it reads as absent. */
const textOrNull = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null);

const decodeFieldEvent = (value: unknown): SegmentFieldEvent | null => {
  const wire = objectOf(value);
  if (typeof wire.event_name !== 'string') return null;

  return {
    eventName: wire.event_name,
    entityType: typeof wire.entity_type === 'string' ? wire.entity_type : null,
    aspect: oneOf(SEGMENT_EVENT_ASPECTS, wire.aspect),
  };
};

const kindOf = (value: unknown): SegmentField['kind'] => {
  const kind = oneOf(SEGMENT_FIELD_KINDS, value);

  return kind === 'unknown' ? null : kind;
};

export const decodeSegmentField = (value: unknown): SegmentField => {
  const wire = objectOf(value);

  return {
    fieldKey: text(wire.field_key),
    displayName: text(wire.display_name, text(wire.field_key)),
    dataType: oneOf(SEGMENT_DATA_TYPES, wire.data_type),
    allowedOperators: Array.isArray(wire.allowed_operators)
      ? wire.allowed_operators.filter((operator): operator is string => typeof operator === 'string')
      : [],
    // A server from before retirement sends neither, which reads as offered.
    retiredAt: typeof wire.retired_at === 'string' ? wire.retired_at : null,
    retiredReason: typeof wire.retired_reason === 'string' ? wire.retired_reason : null,
    // A server from before field presentation sends none of these: the field reads as `other`, with no family and no
    // kind, and a category or kind this Studio does not know reads the same way. A kind is never guessed: whether a
    // negative condition includes people with no value depends on it.
    category: oneOfOr(SEGMENT_FIELD_CATEGORIES, wire.category, 'other'),
    family: textOrNull(wire.family),
    memberLabel: textOrNull(wire.member_label),
    kind: kindOf(wire.kind),
    isArray: wire.is_array === true,
    event: decodeFieldEvent(wire.event),
    helpText: textOrNull(wire.help_text),
  };
};

export const decodeSegmentNode = (value: unknown): SegmentNode => {
  const wire = objectOf(value);

  if (wire.kind === 'group') return decodeSegmentGroup(wire) ?? { kind: 'unknown', wire: value };
  if (wire.kind === 'condition' && typeof wire.field_key === 'string' && typeof wire.operator === 'string') {
    const condition: SegmentCondition = { kind: 'condition', fieldKey: wire.field_key, operator: wire.operator };

    return wire.value === undefined || wire.value === null
      ? condition
      : { ...condition, value: wire.value as SegmentCondition['value'] };
  }
  if (wire.kind === 'segment_ref' && typeof wire.ref_segment_id === 'string') {
    return { kind: 'segment_ref', refSegmentId: wire.ref_segment_id, negate: wire.negate === true };
  }

  return { kind: 'unknown', wire: value };
};

/**
 * `null` for anything but a group, and for a group whose combinator this Studio does not know: reading either as a
 * group would change the segment the next time it was saved, so it is kept as it came (nested) or makes the segment
 * read-only (the root). A group with no `negate` (an older server) is not negated.
 */
export function decodeSegmentGroup(value: unknown): SegmentGroup | null {
  const wire = objectOf(value);
  if (wire.kind !== 'group') return null;

  const combinator = oneOf(SEGMENT_COMBINATORS, wire.combinator);
  if (combinator === 'unknown') return null;

  return {
    kind: 'group',
    combinator,
    negate: wire.negate === true,
    children: Array.isArray(wire.children) ? wire.children.map(decodeSegmentNode) : [],
  };
}

export const decodeSegmentSummary = (value: unknown): SegmentSummary => {
  const wire = objectOf(value);

  return {
    id: text(wire.id),
    name: text(wire.name),
    description: typeof wire.description === 'string' ? wire.description : null,
    isReusable: wire.is_reusable === true,
    status: oneOf(SEGMENT_STATUSES, wire.status),
    source: oneOf(SEGMENT_SOURCES, wire.source),
    updatedAt: text(wire.updated_at),
    version: typeof wire.version === 'number' ? wire.version : null,
  };
};

/** A negated root is not drawn by the builder, so it reads like a root this Studio cannot read: the segment is read-only. */
const decodeRoot = (value: unknown): SegmentGroup | null => {
  const root = decodeSegmentGroup(value);

  return root?.negate ? null : root;
};

export const decodeSegmentDetail = (value: unknown): SegmentDetail => {
  const wire = objectOf(value);

  return {
    ...decodeSegmentSummary(wire),
    root: wire.root === null || wire.root === undefined ? null : decodeRoot(wire.root),
  };
};

export const encodeSegmentNode = (node: SegmentNode): unknown => {
  switch (node.kind) {
    case 'group':
      // `negate` is always sent: the server reads its presence as "this client knows exclusion groups".
      return {
        kind: 'group',
        combinator: node.combinator,
        negate: node.negate,
        children: node.children.map(encodeSegmentNode),
      };
    case 'condition':
      return {
        kind: 'condition',
        field_key: node.fieldKey,
        operator: node.operator,
        ...(node.value === undefined ? {} : { value: node.value }),
      };
    case 'segment_ref':
      return { kind: 'segment_ref', ref_segment_id: node.refSegmentId, negate: node.negate };
    default:
      return node.wire;
  }
};

export const encodeSegmentCreate = (create: SegmentCreate): Wire => ({
  name: create.name,
  ...(create.description === undefined ? {} : { description: create.description }),
  ...(create.isReusable === undefined ? {} : { is_reusable: create.isReusable }),
  status: create.status,
  root: encodeSegmentNode(create.root),
});

/** Only the fields being changed, so the PATCH keeps what is stored for the rest. */
export const encodeSegmentUpdate = (update: SegmentUpdate): Wire => ({
  expected_version: update.expectedVersion,
  ...(update.name === undefined ? {} : { name: update.name }),
  ...(update.description === undefined ? {} : { description: update.description }),
  ...(update.isReusable === undefined ? {} : { is_reusable: update.isReusable }),
  ...(update.status === undefined ? {} : { status: update.status }),
  ...(update.root === undefined ? {} : { root: encodeSegmentNode(update.root) }),
});

export const decodeSegmentPreview = (value: unknown): SegmentPreview => {
  const wire = objectOf(value);

  return {
    id: text(wire.id),
    state: oneOf(SEGMENT_PREVIEW_STATES, wire.state),
    count: typeof wire.count === 'number' ? wire.count : null,
    countedAt: typeof wire.counted_at === 'string' ? wire.counted_at : null,
    reused: wire.reused === true,
    failure: wire.failure === undefined || wire.failure === null ? null : oneOf(SEGMENT_PREVIEW_FAILURES, wire.failure),
  };
};

/**
 * A count and its time come together or not at all, so a half-pair reads as no count. A failure with no readable
 * `since` is no failure: the state alone then says the refresh failed.
 */
export const decodeSegmentCount = (value: unknown): SegmentCount => {
  const wire = objectOf(value);
  const failure = objectOf(wire.refresh_failure);
  const hasPair = typeof wire.count === 'number' && typeof wire.counted_at === 'string';

  return {
    state: oneOf(SEGMENT_COUNT_STATES, wire.state),
    count: hasPair ? (wire.count as number) : null,
    countedAt: hasPair ? (wire.counted_at as string) : null,
    failure:
      typeof failure.since === 'string'
        ? { errorCode: oneOf(SEGMENT_REFRESH_ERROR_CODES, failure.error_code), since: failure.since }
        : null,
  };
};
